// Web Push registration/subscription — the channel that survives the browser
// being fully closed, unlike the plain Notification API in
// browser-notification.ts (which only fires while some tab is open). Kept
// deliberately separate: every call here can fail for reasons that have
// nothing to do with permission (no service worker support, a flaky
// subscribe() call, the backend being unreachable), and a failure here must
// never block reminder creation — the plain-Notification path stays as the
// foreground/background-tab fallback regardless of what happens here.

import {
  registerPushSubscription,
  unregisterPushSubscription
} from "../../containers/Notifications/api/services"

export const isWebPushSupported = (): boolean =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window

// The VAPID public key arrives base64url-encoded (browser-safe: '-'/'_', no
// padding); PushManager.subscribe() needs it as a raw Uint8Array.
function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0))
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isWebPushSupported()) return null
  try {
    return await navigator.serviceWorker.register("/sw.js")
  } catch {
    return null
  }
}

// Idempotent: subscribe() on an already-subscribed registration returns the
// existing subscription rather than creating a second one.
export async function subscribeToWebPush(): Promise<boolean> {
  const vapidPublicKey = import.meta.env.VITE_APP_VAPID_PUBLIC_KEY
  if (!isWebPushSupported() || !vapidPublicKey) return false

  try {
    const registration = await registerServiceWorker()
    if (!registration) return false

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // A DOM-lib-version quirk: PushSubscriptionOptions wants a BufferSource
      // backed by a plain ArrayBuffer, while Uint8Array.from()'s return type
      // is generic over ArrayBufferLike (which includes SharedArrayBuffer) —
      // this Uint8Array is always ArrayBuffer-backed in practice.
      applicationServerKey: base64UrlToUint8Array(vapidPublicKey) as BufferSource
    })

    const response = await registerPushSubscription({
      ...subscription.toJSON(),
      user_agent: navigator.userAgent
    } as PushSubscriptionJSON & { user_agent: string })

    return response.ok
  } catch {
    return false
  }
}

export async function unsubscribeFromWebPush(): Promise<void> {
  if (!isWebPushSupported()) return

  try {
    const registration = await navigator.serviceWorker.getRegistration("/sw.js")
    const subscription = await registration?.pushManager.getSubscription()
    if (!subscription) return

    await unregisterPushSubscription(subscription.endpoint)
    await subscription.unsubscribe()
  } catch {
    // Best-effort cleanup (logout / settings toggle) — nothing to recover
    // to, and a stale subscription just goes unused server-side.
  }
}
